import React, { createContext, useContext, useEffect, useState } from 'react';
import { userApi } from '../services/api';

interface ThemeContextType {
  theme: 'light' | 'dark' | 'system';
  actualTheme: 'light' | 'dark'; // The actual theme being applied (resolved from system if needed)
  setTheme: (theme: 'light' | 'dark' | 'system') => Promise<void>;
  loadThemeFromAPI: () => Promise<void>;
  isLoading: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<'light' | 'dark' | 'system'>(() => {
    // Initialize from localStorage immediately
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' | 'system' | null;
    return savedTheme || 'system';
  });
  const [systemTheme, setSystemTheme] = useState<'light' | 'dark'>(() => {
    // Detect initial system theme
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });
  const [isLoading, setIsLoading] = useState(false);

  // Calculate the actual theme to apply
  const actualTheme = theme === 'system' ? systemTheme : theme;

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Apply theme to document
  useEffect(() => {
    if (actualTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [actualTheme]);

  const loadThemeFromAPI = async () => {
    try {
      setIsLoading(true);
      // Load from user profile to get the authoritative theme
      const response = await userApi.getProfile();
      if (response.success && response.data?.theme) {
        const userTheme = response.data.theme as 'light' | 'dark' | 'system';
        if (userTheme !== theme) {
          setThemeState(userTheme);
          localStorage.setItem('theme', userTheme);
        }
      }
    } catch (error) {
      console.error('Error loading theme from API:', error);
      // Keep current theme from localStorage
    } finally {
      setIsLoading(false);
    }
  };

  const setTheme = async (newTheme: 'light' | 'dark' | 'system') => {
    try {
      // Update UI immediately
      setThemeState(newTheme);
      localStorage.setItem('theme', newTheme);
      
      // Save to database
      await userApi.updatePreferences({ theme: newTheme });
    } catch (error) {
      console.error('Error updating theme preference:', error);
      throw error;
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, actualTheme, setTheme, loadThemeFromAPI, isLoading }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}